import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import Image from 'components/Common/Image'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import LayoutContainer from 'components/Common/LayoutContainer'
import ShareModal from 'components/Common/Modals/ShareModal'
import { getShareModalConfig } from 'lib/getShareModalConfig'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import LastMinuteSummary from './LastMinuteSummary'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import OfferBNPLAd from '../OfferBNPLAd'
import SocialProofWidget from '../SocialProofWidget'
import { isShowPageViewsEnabled, isShowTotalPurchasesEnabled } from 'selectors/socialWidgetSelector'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import TextButton from 'components/Luxkit/Button/TextButton'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import TextLink from 'components/Luxkit/TextLink'
import OfferDescription from '../OfferDescription'
import BodyText from 'components/Luxkit/Typography/BodyText'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import config from 'constants/config'
import SolidPlayIcon from 'components/Luxkit/Icons/solid/SolidPlayIcon'
import { isIntroMapEnabled } from 'selectors/featuresSelectors'
import PropertyMap from 'components/Common/PropertyMap/PropertyMap'
import { OFFER_SUMMARY_MAP_HEIGHT, PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import Breakpoint from 'components/Common/Breakpoint/Breakpoint'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import OfferPagePropertyTitle from '../OfferPagePropertyTitle'

const OfferIntroGrid = styled(LayoutContainer)`
  display: grid;
  padding: ${rem(20)};
  grid-template-columns: 1fr;
  grid-gap: ${rem(12)};

  ${mediaQueryUp.sm} {
    grid-template-columns: 1fr 1fr;
    padding: ${rem(20)} ${rem(40)};
  }

  ${mediaQueryUp.md} {
    padding: ${rem(30)} ${rem(12)};
    grid-gap: ${rem(16)} ${rem(40)};
    grid-template-columns: 1fr min-content;
    grid-template-rows: min-content min-content min-content min-content 1fr;
  }
`

const OfferBadgeIntroWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-row: 4;

  ${mediaQueryUp.sm} {
    grid-row: 3;
    grid-column: 1;
  }
`

const OfferBadgeTagSection = styled(BodyText)`

  > * {
    margin: 0;
  }
`

const OfferIntroSection = styled(BodyText)`
  margin-top: ${rem(16)};
`

const BNPLAd = styled(OfferBNPLAd)`
  margin-top: ${rem(24)};
  padding-top: ${rem(24)};
  border-top: 1px solid ${props => props.theme.palette.neutral.five};

  ${mediaQueryUp.sm} {
    display: none;
  }
`

const SummaryBlock = styled.div`
  display: none;

  ${mediaQueryUp.sm} {
    position: sticky;
    /* height of the filter bar */
    top: ${rem(64)};
    display: block;
    align-self: flex-start;
    grid-row: 3;
    grid-column: 2;
  }

  ${mediaQueryUp.md} {
    grid-row: 2 / span 3;
    grid-column: 2;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const HotelSummary = styled(LastMinuteSummary)`
  display: block;
  align-self: flex-start;
  min-width: ${rem(290)};

  ${mediaQueryUp.md} {
    min-width: ${rem(370)};
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${mediaQueryUp.sm} {
    grid-row: 2;
    grid-column: span 2;
  }

  ${mediaQueryUp.md} {
    grid-column: 1;
  }
`

const OfferOtherActions = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  grid-row: 3;
  grid-column: 1;

  ${mediaQueryUp.sm} {
    justify-content: flex-end;
    grid-row: 1;
    grid-column: 2;
  }
`

const PropertyLogo = styled(Image)`
  display: none;

  ${mediaQueryUp.md} {
    margin-left: ${rem(16)};
    display: block;
    height: ${rem(75)};
  }
`

const WatchVideoButton = styled(TextButton)`
  margin-top: ${rem(12)};
`

interface Props {
  offer: App.Offer;
  customerPortalURL: string;
  regionCode: string;
  pathname: string;
  memberId: string;
  onWatchVideo: () => void;
  className?: string;
  onViewPackageOptions: () => void;
  showSocialWidget: boolean;
  isShowPageViewsEnabled: boolean;
  isShowTotalPurchasesEnabled: boolean;
  flightDestination?: App.Airport;
  isMapEnabled: boolean;
}

function LastMinuteIntro(props: Props) {
  const {
    offer,
    onWatchVideo,
    memberId,
    pathname,
    regionCode,
    customerPortalURL,
    className,
    onViewPackageOptions,
    showSocialWidget,
    isShowPageViewsEnabled,
    isShowTotalPurchasesEnabled,
    flightDestination,
    isMapEnabled,
  } = props

  const {
    pageViews,
    totalPurchases,
  } = offer

  const showABTestWidget = isShowPageViewsEnabled || isShowTotalPurchasesEnabled
  const showPageViews = isShowPageViewsEnabled && pageViews && Number(pageViews) > 200
  const showTotalPurchases = isShowTotalPurchasesEnabled && totalPurchases && Number(totalPurchases) > 5
  const property = offer.property
  const location = [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')

  const showBadge = !!offer.badge && config.BRAND === 'luxuryescapes'

  const shareConfig = getShareModalConfig({
    customerPortalURL,
    image: offer.images[0],
    memberId,
    offerId: offer.id,
    offerLocation: location,
    pathname,
    region: regionCode,
    type: 'offer',
    utmCampaign: 'webshare',
  })

  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])

  return (
    <OfferIntroGrid className={className} data-testid="offer-intro">
      {
        showSocialWidget && showABTestWidget && (showPageViews || showTotalPurchases) &&
          <SocialProofWidget
            showPageViews={showPageViews}
            showTotalPurchases={showTotalPurchases}
            totalPurchases={totalPurchases}
            pageViews={pageViews}
          />
      }
      <OfferUrgencyLabels offer={offer} showLimitedAvailability />
      <OfferOtherActions>
        <TripGuard inverse>
          <ShareModal shareConfig={shareConfig}>
            <ShareWithFriendsButton outdent="right" />
          </ShareModal>
        </TripGuard>
        <TripGuard>
          <ShareModal shareConfig={shareConfig}>
            <ShareWithFriendsButton />
          </ShareModal>
          <BookmarkButton offer={offer} />
        </TripGuard>
      </OfferOtherActions>
      <Header>
        <VerticalSpacer gap={8}>
          <BodyTextBlock variant="medium" data-testid="offer-location" startIcon={<LineMapMarkerIcon />}>
            {location} • <TextLink href="#getting-there">view map</TextLink>
          </BodyTextBlock>
          <OfferPagePropertyTitle propertyName={offer.property.name} />
          <Subtitle variant="subtitle2" as="h2">{offer.name}</Subtitle>
        </VerticalSpacer>
        {property.logoImageId &&
          <PropertyLogo
            dpr={2}
            height={75}
            enhancedFormats={false}
            id={property.logoImageId}
            alt={`${property.name || 'Vendor'} logo`}
          />}
      </Header>
      <OfferBadgeIntroWrapper>
        {showBadge && <OfferBadgeTagSection variant="medium">
          <OfferBadgeTag badge={offer.badge} />
        </OfferBadgeTagSection>}
        <OfferIntroSection variant="medium">
          {offer.description && <OfferDescription description={offer.description} />}
          {offer.additionalDescription && <MarkdownRender type="compact" content={offer.additionalDescription} />}
          {isMapEnabled && property && <Breakpoint max="xs">
            <PropertyMap
              height={OFFER_SUMMARY_MAP_HEIGHT}
              wrapperStyle={{ height: OFFER_SUMMARY_MAP_HEIGHT }}
              markers={[{
                id: property.id,
                type: PROPERTY_MAP_MARKER_TYPE,
                latitude: property.latitude,
                longitude: property.longitude,
              }]} />
          </Breakpoint>}
          {offer.vimeoVideoId &&
            <WatchVideoButton kind="secondary" startIcon={ <SolidPlayIcon />} onClick={onWatchVideo}>
              Play video
            </WatchVideoButton>
        }
        </OfferIntroSection>
      </OfferBadgeIntroWrapper>
      <SummaryBlock>
        <div>
          <HotelSummary onViewPackageOptions={onViewPackageOptions} flightDestination={flightDestination} />
          {isMapEnabled && property && <PropertyMap
            height={OFFER_SUMMARY_MAP_HEIGHT}
            wrapperStyle={{ height: OFFER_SUMMARY_MAP_HEIGHT }}
            markers={[{ id: property.id, type: PROPERTY_MAP_MARKER_TYPE, latitude: property.latitude, longitude: property.longitude }]}
          />}
        </div>
        <OfferBNPLAd prices={allPrices} />
      </SummaryBlock>
      <BNPLAd prices={allPrices} />
    </OfferIntroGrid>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.offer,
  regionCode: appState.geo.currentRegionCode,
  pathname: appState.router.location.pathname,
  customerPortalURL: appState.config.customerPortalURL,
  memberId: appState.auth.account.memberId,
  showSocialWidget: appState.ui.socialWidget.display,
  isShowPageViewsEnabled: isShowPageViewsEnabled(appState),
  isShowTotalPurchasesEnabled: isShowTotalPurchasesEnabled(appState),
  isMapEnabled: isIntroMapEnabled(appState),
}))(LastMinuteIntro)
